import React, { useEffect, useState } from 'react';
import HeroSlider from '../components/HeroSlider';
import SideBanner from '../components/SideBanner';
import CategoryCards from '../components/CategoryCards';
import { Link } from 'react-router-dom';
import GameTile from '../components/GameTile';
import GameArtwork from '../components/GameArtwork';
import GameModal from '../components/GameModal';
import GameChoiceModal from '../components/GameChoiceModal';
import AuthModal from '../components/AuthModal';
import {
  ChevronRight, Trophy, Play,
  Flame, Video, Sparkles,
} from 'lucide-react';
import client from '../lib/api';
import { useGameLauncher } from '../hooks/useGameLauncher';
import { useSettings } from '../context/SettingsContext';
import { slidersAt } from '../lib/sliderPlacement';
import { SECTIONS, sectionHook } from '../lib/domHooks';
import HomeBlocks, { HomeBlock } from '../components/HomeBlocks';

// Ana sayfa vitrin sırası: Yeni Slotlar → Canlı Casino → Popüler Slotlar.
const LIVE_CASINO_COUNT = 10;
const POPULAR_SLOT_COUNT = 10;
const NEW_SLOT_COUNT = 12;

const Section = ({ title, icon: Icon, action, section, children }) => (
  <section className="home-section mt-9 md:mt-11" {...sectionHook(section)}>
    <div className="flex items-center justify-between mb-4 md:mb-5">
      <h2 className="home-section-title text-lg md:text-2xl font-black flex items-center gap-2.5">
        {Icon && <span className="home-section-icon"><Icon size={17} /></span>}
        <span>{title}</span>
      </h2>
      {action}
    </div>
    {children}
  </section>
);

const Home = () => {
  const [slots, setSlots] = useState([]);
  const [live, setLive] = useState([]);
  const [featuredSlots, setFeaturedSlots] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [sports, setSports] = useState([]);
  const [banners, setBanners] = useState([]);
  const gl = useGameLauncher();
  const settings = useSettings();

  useEffect(() => {
    client.get('/sliders').then(({ data }) => setBanners(data.items || [])).catch(() => setBanners([]));
    // Vitrin şeritleri panelden seçilir (Oyun Yönetimi → Ana Sayfa Vitrini);
    // seçim yoksa uç otomatik kaynakla doldurur, bölüm boş kalmaz.
    client.get('/games/showcase', { params: { strip: 'new', category: 'slot', limit: NEW_SLOT_COUNT } })
      .then(({ data }) => setSlots(data.items || [])).catch(() => {});
    client.get('/games', { params: { category: 'live-casino', limit: LIVE_CASINO_COUNT } }).then(({ data }) => setLive(data.items || [])).catch(() => {});
    let active = true;
    client.get('/games/showcase', { params: { strip: 'popular', category: 'slot', limit: POPULAR_SLOT_COUNT } })
      .then(({ data }) => {
        if (!active) return;
        setFeaturedSlots(data.items || []);
      })
      .catch(() => {
        if (active) setFeaturedSlots([]);
      })
      .finally(() => {
        if (active) setFeaturedLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!settings.settingsLoaded) return;
    if (settings.sports_enabled === false) {
      setSports([]);
      return;
    }
    client.get('/sports/matches')
      .then(({ data }) => setSports((data.matches || []).slice(0, 4)))
      .catch(() => setSports([]));
  }, [settings.settingsLoaded, settings.sports_enabled]);

  const newSlots = slots.slice(0, NEW_SLOT_COUNT);
  const liveTop = live.slice(0, LIVE_CASINO_COUNT);
  const popularSlots = featuredSlots.slice(0, POPULAR_SLOT_COUNT);
  const visibleBanners = banners.filter(
    (banner) => banner.cta_link !== '/spor' || settings.sports_enabled !== false,
  );
  const sideBanners = slidersAt(visibleBanners, 'side');

  const mapForTile = (g) => ({
    id: g.id, name: g.name, provider: g.provider, img: g.image,
    hot: g.hot, new: g.new, api_provider: g.api_provider, game_id: g.game_id,
    demo_supported: !!g.demo_supported,
  });

  return (
    <div className="pusu-body-gradient">
      <div className="home-shell max-w-7xl mx-auto px-3 sm:px-4 pt-4 md:pt-5">
        <HomeBlocks theme="default">
          <HomeBlock id={SECTIONS.HERO}>
          <div
            className={`home-hero-layout ${sideBanners.length > 0 ? '' : 'is-single'}`}
            {...sectionHook(SECTIONS.HERO)}
          >
            <HeroSlider onPlayGame={gl.play} />
            {sideBanners.length > 0 && <SideBanner banners={sideBanners} />}
          </div>
          </HomeBlock>

          <HomeBlock id={SECTIONS.CATEGORIES}>
          <div className="mt-4 md:mt-5" {...sectionHook(SECTIONS.CATEGORIES)}>
            <CategoryCards />
          </div>
          </HomeBlock>

          <HomeBlock id={SECTIONS.NEW}>
          {newSlots.length > 0 && (
            <Section section={SECTIONS.NEW} title="Yeni Slotlar" icon={Sparkles} action={<Link to="/slotlar" className="home-section-link">Tümünü Gör <ChevronRight size={14} /></Link>}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {newSlots.map((g) => <GameTile key={g.id} game={mapForTile(g)} onPlay={gl.play} />)}
              </div>
            </Section>
          )}
          </HomeBlock>

          <HomeBlock id={SECTIONS.LIVE_CASINO}>
          {liveTop.length > 0 && (
            <Section section={SECTIONS.LIVE_CASINO} title="Canlı Casino" icon={Video} action={<Link to="/canli-casino" className="home-section-link">Tümünü Gör <ChevronRight size={14} /></Link>}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
                {liveTop.map((g) => (
                  <div key={g.id} className="game-tile game-tile--portrait" onClick={() => gl.play(g)}>
                    <GameArtwork src={g.image} alt={g.name} />
                    <span className="absolute top-2 left-2 live-badge z-10"><span className="live-dot"/>CANLI</span>
                    <div className="overlay"><button className="gold-btn rounded-full w-14 h-14 flex items-center justify-center"><Play size={22} fill="var(--pusu-on-accent)" /></button></div>
                    <div className="absolute inset-x-0 bottom-0 p-2.5 z-10" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.9))' }}>
                      <div className="text-xs md:text-sm font-semibold truncate">{g.name}</div>
                      <div className="text-[10px] md:text-xs text-gray-400">{g.provider}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
          </HomeBlock>

          <HomeBlock id={SECTIONS.POPULAR}>
          {popularSlots.length > 0 && (
            <Section
              section={SECTIONS.POPULAR}
              title="Popüler Slotlar"
              icon={Flame}
              action={<Link to="/slotlar" className="home-section-link">Tümünü Gör <ChevronRight size={14} /></Link>}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                {popularSlots.map((g) => <GameTile key={g.id} game={mapForTile(g)} onPlay={gl.play} />)}
              </div>
            </Section>
          )}
          </HomeBlock>

          <HomeBlock id={SECTIONS.MATCHES}>
          {settings.sports_enabled !== false && sports.length > 0 && (
            <Section section={SECTIONS.MATCHES} title="Günün Maçları" icon={Trophy} action={<Link to="/spor" className="home-section-link">Tüm Maçlar <ChevronRight size={14} /></Link>}>
              <div className="pusu-card overflow-hidden">
                {sports.map((m, i) => (
                  <div key={m.id} className={`grid grid-cols-12 items-center gap-3 p-4 ${i !== sports.length - 1 ? 'border-b border-white/5' : ''}`}>
                    <div className="col-span-12 md:col-span-4 flex items-center gap-2">
                      <Trophy size={14} className="text-[var(--pusu-gold)]" />
                      <div>
                        <div className="text-xs text-gray-400">{m.league}</div>
                        <div className="text-xs text-gray-500">{new Date(m.date).toLocaleString('tr-TR', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                      {m.live && <span className="live-badge ml-2"><span className="live-dot"/>{m.elapsed || 'CANLI'}</span>}
                    </div>
                    <div className="col-span-12 md:col-span-4 flex items-center justify-between md:justify-start md:gap-6">
                      <div className="font-semibold text-sm">{m.home}</div>
                      <div className="text-xs text-gray-500">{m.live && m.score_home != null ? `${m.score_home} - ${m.score_away}` : 'vs'}</div>
                      <div className="font-semibold text-sm">{m.away}</div>
                    </div>
                    <div className="col-span-12 md:col-span-4 flex gap-2 justify-end">
                      {['1', 'X', '2'].map((k) => (
                        <button key={k} className="odds-btn"><span className="label">{k}</span><span className="val">{Number(m.odds[k]).toFixed(2)}</span></button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
          </HomeBlock>
        </HomeBlocks>

        {featuredLoading && liveTop.length === 0 && (
          <section className="home-section mt-9 md:mt-11 pb-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="home-section-title text-lg md:text-2xl font-black flex items-center gap-2.5">
                <span className="home-section-icon"><Flame size={17} /></span>
                <span>Popüler Slotlar</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-3 md:gap-4" aria-label="Oyunlar yükleniyor">
              {[0, 1, 2, 3, 4, 5, 6].map((item) => <div key={item} className="home-game-skeleton" />)}
            </div>
          </section>
        )}
      </div>

      <GameChoiceModal
        game={gl.choice} user={gl.user} balance={gl.balance} bonusBalance={gl.bonusBalance}
        demoAvailable={gl.demoAvailable}
        onClose={gl.closeChoice} onReal={gl.startReal} onDemo={gl.startDemo}
        onLogin={() => gl.openAuth('login')} onRegister={() => gl.openAuth('register')}
      />
      {gl.activeGame && (
        <GameModal key={`${gl.activeGame.id}-${gl.mode}`} game={gl.activeGame}
                   mode={gl.mode} onClose={gl.closeGame} />
      )}
      <AuthModal open={!!gl.authTab} onClose={() => gl.setAuthTab(null)} defaultTab={gl.authTab || 'login'} />
    </div>
  );
};

export default Home;
